import React from 'react';
import { Upload, Layers, Zap } from 'lucide-react';
export function HowGuardrailsGetBuilt() {
  return <section className="pt-12 pb-24 px-6 md:px-12 lg:px-24 bg-white" data-id="element-707">
      <div className="max-w-7xl mx-auto" data-id="element-708">
        <div className="text-center mb-16" data-id="element-709">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 font-['Syne'] text-black" data-id="element-710">
            How Context Guardrails Are Built
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto" data-id="element-711">
            Security guardrails decide whether AI is allowed to run. Context guardrails decide how it is allowed to think. Those invisible decisions? Synthia governs them by distilling your
            institutional knowledge into machine-readable attributes. Here's how:
          </p>
        </div>

        {/* Step 1 */}
        <div className="mb-20" data-id="element-712">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-713">
            <div data-id="element-714">
              <div className="flex items-center gap-3 mb-4" data-id="element-715">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-716">
                  <Upload className="w-5 h-5 text-[#0066FF]" data-id="element-717" />
                </div>
                <h3 className="text-2xl font-bold font-['Syne'] text-black" data-id="element-718">
                  STEP 1: GUARDRAIL ENRICHMENT
                </h3>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed" data-id="element-719">
                Start with what you already have: compliance documents, brand
                guidelines, SOPs, training materials, expert communications.
                Upload PDFs, paste text, or add URLs. Synthia extracts the
                reasoning patterns, constraints, and decision frameworks
                embedded in your institutional knowledge—turning tacit expertise
                into structured rules.
              </p>
            </div>
            <div className="rounded-lg overflow-hidden" data-id="element-720">
              <img src="https://cdn.magicpatterns.com/uploads/u8VPidG5Wei6F5PX3mwjqM/image_one_enrich.png" alt="Guardrail Enrichment Interface" className="w-[85%] h-auto" data-id="element-721" />
            </div>
          </div>
        </div>

        {/* Step 2 */}
        <div className="mb-20" data-id="element-722">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-723">
            <div data-id="element-724">
              <div className="flex items-center gap-3 mb-4" data-id="element-725">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center" data-id="element-726">
                  <Layers className="w-5 h-5 text-green-600" data-id="element-727" />
                </div>
                <h3 className="text-2xl font-bold font-['Syne'] text-black" data-id="element-728">
                  STEP 2: DISTILLATION INTO RULES
                </h3>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed mb-4" data-id="element-729">
                Your knowledge gets distilled into atomic, composable
                attributes—discrete rules and constraints that can be activated,
                versioned, and controlled. Each attribute is a building block:
              </p>
              <ul className="space-y-2 mb-4" data-id="element-730">
                <li className="text-gray-700 flex items-start gap-2" data-id="element-731">
                  <span className="text-[#0066FF] mt-1" data-id="element-732">•</span>
                  <span data-id="element-733">Activate or deactivate individual rules</span>
                </li>
                <li className="text-gray-700 flex items-start gap-2" data-id="element-734">
                  <span className="text-[#0066FF] mt-1" data-id="element-735">•</span>
                  <span data-id="element-736">Edit definitions without breaking dependencies</span>
                </li>
                <li className="text-gray-700 flex items-start gap-2" data-id="element-737">
                  <span className="text-[#0066FF] mt-1" data-id="element-738">•</span>
                  <span data-id="element-739">Boost priority for critical constraints</span>
                </li>
                <li className="text-gray-700 flex items-start gap-2" data-id="element-740">
                  <span className="text-[#0066FF] mt-1" data-id="element-741">•</span>
                  <span data-id="element-742">Chat with the guardrail to test reasoning</span>
                </li>
                <li className="text-gray-700 flex items-start gap-2" data-id="element-743">
                  <span className="text-[#0066FF] mt-1" data-id="element-744">•</span>
                  <span data-id="element-745">Version and audit every change</span>
                </li>
              </ul>
              <p className="text-gray-600 italic text-sm leading-relaxed" data-id="element-746">
                Example: This "Compliance Officer – Finance" guardrail contains
                20 attributes covering SEC/FINRA requirements, escalation
                protocols, risk thresholds, and disclosure language. This isn't
                a prompt template. It's institutional intelligence as
                infrastructure.
              </p>
            </div>
            <div className="rounded-lg overflow-hidden" data-id="element-747">
              <img src="https://cdn.magicpatterns.com/uploads/eW8x5z98ouU1GHsdKdoBN8/atomic_attributes.png" alt="Atomic Attributes Interface" className="w-[85%] h-auto" data-id="element-748" />
            </div>
          </div>
        </div>

        {/* Step 3 */}
        <div className="mb-16" data-id="element-749">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-750">
            <div data-id="element-751">
              <div className="flex items-center gap-3 mb-4" data-id="element-752">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center" data-id="element-753">
                  <Zap className="w-5 h-5 text-purple-600" data-id="element-754" />
                </div>
                <h3 className="text-2xl font-bold font-['Syne'] text-black" data-id="element-755">
                  STEP 3: MCP DEPLOYMENT
                </h3>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed" data-id="element-756">
                Once built, guardrails activate automatically through the Model
                Context Protocol. The right attributes reach the model at the
                right time—perfect situational awareness based on role, intent,
                domain, etc. No manual prompting. No hoping the model remembers.
                Just governed reasoning, every time.
              </p>
            </div>
            <div className="relative bg-gray-50 rounded-xl p-8 border border-gray-200 min-h-[400px] flex items-center justify-center" data-id="element-757">
              {/* Chat interface from MCPSection */}
              <div className="relative w-full max-w-sm" data-id="element-758">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 blur-2xl opacity-20 rounded-full" data-id="element-759"></div>
                <div className="relative bg-white p-6 rounded-lg shadow-xl border border-gray-100" data-id="element-760">
                  <div className="flex items-center gap-3 mb-4 border-b border-gray-100 pb-4" data-id="element-761">
                    <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-762"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-400" data-id="element-763"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-764"></div>
                    <span className="ml-auto text-xs text-gray-400" data-id="element-765">
                      Claude / ChatGPT / Gemini
                    </span>
                  </div>
                  <div className="space-y-3" data-id="element-766">
                    <div className="flex gap-3" data-id="element-767">
                      <div className="w-8 h-8 rounded-full bg-gray-200" data-id="element-768"></div>
                      <div className="flex-1 bg-gray-100 rounded p-2 text-xs text-gray-500" data-id="element-769">
                        Draft a response to the client regarding the merger...
                      </div>
                    </div>

                    {/* Synthia Injection */}
                    <div className="ml-11 p-3 bg-blue-50 rounded border border-blue-100 relative" data-id="element-770">
                      <div className="absolute -left-3 top-3 w-3 h-[1px] bg-blue-200" data-id="element-771"></div>
                      <div className="flex items-center gap-2 mb-2" data-id="element-772">
                        <div className="w-4 h-4 rounded bg-[#0066FF] flex items-center justify-center text-[10px] text-white font-bold" data-id="element-773">
                          S
                        </div>
                        <span className="text-xs font-bold text-[#0066FF]" data-id="element-774">
                          Synthia Active
                        </span>
                      </div>
                      <div className="space-y-1" data-id="element-775">
                        <div className="h-1.5 bg-blue-200 rounded w-3/4" data-id="element-776"></div>
                        <div className="h-1.5 bg-blue-200 rounded w-1/2" data-id="element-777"></div>
                      </div>
                    </div>

                    <div className="flex gap-3 flex-row-reverse" data-id="element-778">
                      <div className="w-8 h-8 rounded-full bg-purple-100" data-id="element-779"></div>
                      <div className="flex-1 bg-white border border-gray-200 rounded p-2 text-xs text-gray-600" data-id="element-780">
                        Based on your corporate communication guidelines (Policy
                        4.2), here is a compliant draft...
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Result */}
        <div className="text-center mt-20" data-id="element-781"></div>
      </div>
    </section>;
}